import React from 'react';
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { withRouter } from 'react-router-dom';
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import Paper from '@material-ui/core/Paper';
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import Typography from '@material-ui/core/Typography';
import Switch from "@material-ui/core/Switch";
import { fade } from '@material-ui/core/styles/colorManipulator';
import Skeleton from "@material-ui/lab/Skeleton";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Tooltip as RechartsTooltip,
  ComposedChart,
  Cell,
} from 'recharts';

import Scenario from "../../api/Scenario";
import FilterListView from "../../components/FilterListView";
import Header from "../../components/utils/Header";
import {
  autoFormat,
  floatFormatter,
  formatFloat,
  formatRatioPercent
} from '../../utils';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import { athenaBlue, statGreen, statRed } from '../../globals';
import DataFrameDownload from '../../components/utils/DataFrameDownload';


const styles = theme => ({
  GraphicView: {
    width: "100%"
  },
  tablePaper: {
    overflow: 'auto',
  },
  chartContainer: {
    overflow: 'hidden',
  },
  graphic: {
    height: 500,
    width: "100%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  empty: {
    color: theme.palette.text.disabled,
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  }
});

class CustomizedLabel extends React.Component {

  render () {
    const {x, y, data, width, index} = this.props;
    const row = data[index];
    const diff = row['Scenario 2'] - row['Scenario 1'];
   	return (
   	  <text
        x={x+width/2}
        y={y}
        dy={diff > 0 ? -24 : 36}
        fontSize='14'
        fontFamily='sans-serif'
        fill='black'
        textAnchor="middle"
      >
        {
          row['Scenario 1'] === null || row['Scenario 2'] === null
            ? '—'
            : formatRatioPercent( diff / row['Scenario 1'])
        }
   	  </text>
    );
  }
}

@withStyles(styles)
@withRouter
@observer
class RawAndPacksView extends BaseScenarioDashboardView {
  static apiSubPath = '/raw_and_packs/';

  @observable scenario1 = null;
  @observable scenario2 = null;
  @observable scenario3 = null;
  @observable dateLevel = null;
  @observable granularity = null;

  _setData(data) {
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;
    this.scenario3 = data.scenario3;
    this.dateLevel = data.date_level;
    this.granularity = data.granularity;
  }

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
      scenario3: this.scenario3,
      date_level: this.dateLevel,
      granularity: this.granularity,
    };
  }

  /* We need to create a new object with the values to avoid problems with the labels */
  get graphData() {
    if (this.data === null) {
      return [];
    }
    return this.data.graph_data.map(row => ({
      'Diff S2-S1': [row['Scenario 1'], row['Scenario 2']],
      ...row,
    }));
  }

  onScenario1Change = this.genericChangeHandler('scenario1');
  onScenario2Change = this.genericChangeHandler('scenario2');
  onScenario3Change = this.genericChangeHandler('scenario3');
  onGranularityChange = this.genericChangeHandler('granularity');

  @action onDateLevelChange = event => {
    this.dateLevel = event.target.checked ? 'month' : 'quarter';
    this.updateData();
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={560} height={55} />;
    }
    return (
      <Grid container spacing={1} justify="flex-end" alignItems="center">
        <Grid item>
          <Typography noWrap>
            Quarterly
            <Switch
              checked={this.dateLevel === 'month'}
              onChange={this.onDateLevelChange}
            />
            Monthly
          </Typography>
        </Grid>
        <Grid item>
          {this.renderChoicesField(
            'Granularity', this.data.granularity_choices, this.granularity,
            this.onGranularityChange,
          )}
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 1"
            scenarioId={this.scenario1}
            choices={this.data.scenario1_choices}
            onScenarioChange={this.onScenario1Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 2"
            scenarioId={this.scenario2}
            choices={this.data.scenario2_choices}
            onScenarioChange={this.onScenario2Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 3"
            scenarioId={this.scenario3}
            choices={this.data.scenario3_choices}
            onScenarioChange={this.onScenario3Change}
          />
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/raw_and_packs/?'+ this.getQueryString())}
            permission="athena.download_raw_packs"
          />
        </Grid>
      </Grid>
    );
  }

  get graphic () {
    if (!this.ready) {
      return <Skeleton variant="rect" height={400} />;
    }
    const xAxisDataKey = `${this.dateLevel}_year`;
    return (
      <Paper>
        <ResponsiveContainer aspect={21 / 9}>
          <ComposedChart margin={{ top: 32, left: 32, bottom: 16, right: 16 }} data={this.graphData}>
            <defs>
              <linearGradient id="greenToRed" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={statGreen} />
                <stop offset="50%" stopColor={statGreen} />
                <stop offset="50%" stopColor={statRed} />
                <stop offset="100%" stopColor={statRed} />
              </linearGradient>
            </defs>
            <XAxis dataKey={xAxisDataKey} stroke="#cdcdcd" />
            <YAxis tickFormatter={floatFormatter(0)} domain={['auto', 'auto']} stroke="#cdcdcd" />
            <RechartsTooltip formatter={(value, name) => (
              name === 'Diff S2-S1'
                ? value[0]===null || value[1]===null
                    ? '-'
                    : formatRatioPercent( (value[1] - value[0]) / value[0])
                : formatFloat(value, 0)
            )} />
            <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
            {this.getLine('Scenario 1', { stroke: statRed, strokeWidth: 3 })}
            {this.getLine('Scenario 2', { stroke: athenaBlue, strokeWidth: 3 })}
            {this.getLine('Scenario 3', { stroke: statGreen, strokeWidth: 3, strokeDasharray: '3 3' })}
            {
              this.scenario2 !== null
                ? this.getBar(
                  'Diff S2-S1',
                  {
                    fill: 'url(#greenToRed)',
                    label: <CustomizedLabel data={this.graphData} />,
                    children: this.graphData.map((row, index) =>
                      <Cell
                        key={index}
                        fill={
                          row['Diff S2-S1'][1] > row['Diff S2-S1'][0]
                            ? fade(statGreen, 0.6)
                            : fade(statRed, 0.6)
                        }
                      />
                    )
                  },
                ) : null
            }
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>
    );
  }

  get table () {
    if (!this.ready) {
      return <Skeleton variant="rect" height={300} />;
    }
    const { classes } = this.props;
    return (
      <Paper className={classes.tablePaper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {Object.keys(this.data.table_data[0]).map(column=> (
                column.indexOf('(%)')===-1
                  ? (
                    <TableCell key={column} className={column.indexOf('Total')>-1 ? classes.bold : ''}>
                      {column}
                    </TableCell>
                  ) : null
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {this.data.table_data.map((row, index) => (
              <TableRow key={index}>
                {Object.entries(row).map(([column, value]) => {
                  if(column.indexOf('(%)')>-1){
                    return null;
                  }
                  if(column.indexOf('Delta')>-1){
                    const valuePercent = row[column.split(' ').join(' (%) ')];
                    return(
                      <TableCell key={column} className={column.indexOf('Total')>-1 ? classes.bold : ''}>
                        {value===null
                          ? '—'
                          : (
                            <ConditionalFormat value={valuePercent} name="raw-packs-overview">
                              {autoFormat(value, 0)} ({formatRatioPercent(valuePercent)})
                            </ConditionalFormat>
                          )
                        }
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell key={column} className={column.indexOf('Total')>-1 ? classes.bold : ''}>
                      {autoFormat(value, 0)}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  render () {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Raw & Packs Overview" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        <Grid item className={classes.chartContainer}>
          {this.graphic}
        </Grid>
        <Grid item>
          {this.table}
        </Grid>
      </Grid>
    );
  }
}

export default RawAndPacksView;
